import { apiClient } from './client';
import { LoginResponse } from '../types';

export interface SendLoginLinkRequest {
  email: string;
}

export interface SendLoginLinkResponse {
  success: boolean;
  reason?: string;
}

export interface ExchangeMagicLinkTokenRequest {
  loginToken: string;
}

/**
 * Trigger Django to send a magic-link (passwordless) sign-in email.
 *
 * Apigee's /v1/auth/send-login-link is a passthrough to Django's
 * /api/v4/send-login-link/. Django always returns { success: true } on 200,
 * regardless of account state — unknown emails and 2FA-enabled users get
 * warning emails instead of a real sign in link, but the API response is
 * identical so the client cannot enumerate accounts.
 *
 * 403 (rate-limited) and 5xx errors surface as thrown axios errors.
 */
export const sendLoginLink = async (
  request: SendLoginLinkRequest,
): Promise<SendLoginLinkResponse> => {
  const response = await apiClient.post<SendLoginLinkResponse>(
    '/v1/auth/send-login-link',
    { email: request.email },
  );
  return response.data;
};

/**
 * Redeem a sesame login_token from a magic-link email for Apigee-minted JWTs.
 *
 * Apigee's /v1/auth/token-login-exchange calls Django's SesameLogin view with
 * a mobile User-Agent (so it returns JSON), decodes the returned Django JWT
 * to extract user_uuid, then mints fresh Apigee access + refresh tokens with
 * the same GenerateJWT chain used by /v1/auth/login. Downstream code
 * (validate, refresh, logout) is identical.
 *
 * Returns 401 for expired/reused tokens and for 2FA-enabled users (Django
 * blocks passwordless login when 2FA is on).
 */
export const exchangeMagicLinkToken = async (
  request: ExchangeMagicLinkTokenRequest,
): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>(
    '/v1/auth/token-login-exchange',
    { login_token: request.loginToken },
  );
  return response.data;
};
